import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import logoDark from "../../../assets/images/logo-dark.svg";
import logoLight from "../../../assets/images/logo-light.svg";
import { useFormik } from "formik";
import * as Yup from "yup";
import { createNewPartner } from "../../../slices/partner/action";
import { alertSuccess } from "../../../helpers/general";
import { alertError } from "../../../helpers/errorHandling";
import { set } from "react-hook-form";

const Register = ({ theme }) => {
  const history = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      partnerName: "",
      primaryContactName: "",
      email: "",
      phoneNumber: "",
    },
    validationSchema: Yup.object({
      partnerName: Yup.string().required("Please Enter Your Partner Name"),
      primaryContactName: Yup.string().required(
        "Please Enter Your Primary Contact Name"
      ),
      email: Yup.string()
        .required("Please Enter Your Email")
        .email("Invalid email format"),
      phoneNumber: Yup.string()
        .required("Please Enter Your Phone Number")
        .matches(/^\d{10}$/, "Phone number must be 10 digits"),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        const res = await createNewPartner(values);

        if (res?._id) {
          setRegistrationSuccess(true);
        } else {
          alertError({ message: "Failed to Register Partner" });
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        alertError({ message: "Failed to Register Partner" });
      }
    },
  });

  return (
    <Container>
      <Row className="border-bottom">
        <Col
          md="7"
          className="m-auto d-flex justify-content-between align-items-center py-4"
        >
          <Link
            to="/"
            className={`logo ${theme === "dark" ? "logo-light" : "logo-dark"}`}
          >
            <img
              src="https://assets.danubehome.com/media/assets/svg-v2/logoEn.svg"
              alt="logo"
              height="22"
            />
          </Link>
        </Col>
      </Row>

      <Row>
        <Col
          md="3"
          className="position-absolute top-50 start-50 translate-middle"
        >
          {!registrationSuccess && (
            <div className="px-2 pb-3 text-center">
              <h4>Get Started with Us</h4>
            </div>
          )}

          {registrationSuccess && (
            <div className="alert alert-success p-4 rounded-lg shadow-sm">
              <h4 className="alert-heading">Registration Successful!</h4>
              <p>
                Your account has been created successfully. Please verify your
                email and start using our services.
              </p>
              <hr />
            </div>
          )}

          {!registrationSuccess && (
            <>
              <Card>
                <Form
                  action="#"
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                >
                  <div className="p-5">
                    <Row>
                      <Col md={12} className="mb-3">
                        <label className="form-label">Partner Name</label>
                        <Input
                          name="partnerName"
                          className="form-control"
                          placeholder="Enter Partner Name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.partnerName}
                          invalid={
                            validation.touched.partnerName &&
                            validation.errors.partnerName
                          }
                        />
                        {validation.touched.partnerName &&
                        validation.errors.partnerName ? (
                          <FormFeedback type="invalid">
                            {validation.errors.partnerName}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col md={12} className="mb-3">
                        <label className="form-label">
                          Primary Contact Name
                        </label>
                        <Input
                          name="primaryContactName"
                          className="form-control"
                          placeholder="Enter Primary Contact Name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.primaryContactName}
                          invalid={
                            validation.touched.primaryContactName &&
                            validation.errors.primaryContactName
                          }
                        />
                        {validation.touched.primaryContactName &&
                        validation.errors.primaryContactName ? (
                          <FormFeedback type="invalid">
                            {validation.errors.primaryContactName}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col md={12} className="mb-3">
                        <label className="form-label">Email</label>
                        <Input
                          name="email"
                          type="email"
                          className="form-control"
                          placeholder="Enter Email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email}
                          invalid={
                            validation.touched.email && validation.errors.email
                          }
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid">
                            {validation.errors.email}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col md={12} className="mb-3">
                        <label className="form-label">Phone Number</label>
                        <Input
                          name="phoneNumber"
                          className="form-control"
                          placeholder="Enter Phone Number"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.phoneNumber}
                          invalid={
                            validation.touched.phoneNumber &&
                            validation.errors.phoneNumber
                          }
                        />
                        {validation.touched.phoneNumber &&
                        validation.errors.phoneNumber ? (
                          <FormFeedback type="invalid">
                            {validation.errors.phoneNumber}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col md={12} className="mb-3">
                        <Button
                          size="md"
                          color="primary"
                          className="w-100"
                          type="submit"
                        >
                          {isLoading ? (
                            <Spinner size="sm" className="me-2">
                              Loading...
                            </Spinner>
                          ) : null}
                          Register
                        </Button>
                      </Col>
                    </Row>
                  </div>
                </Form>
              </Card>
              <div className="d-flex justify-content-center align-items-center gap-3 fs-13">
                <Link
                  className="text-info text-decoration-underline"
                  to="/login"
                >
                  Already have an account? Sign In
                </Link>
              </div>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default Register;
